import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store/redux/store/store';
import { Roles } from '../../../../models/general';
import { ShieldCheck, Building, Landmark, HeartPulse, AlertTriangle, Info, Clock, CalendarDays, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../../../components/ui/Card';
import { PageLayout } from '../../../../components/ui/PageLayout';
import Input from '../../../../components/ui/Input';
import Button from '../../../../components/ui/Button';
import { useToast, ToastRenderer } from '../../../../hooks/useToast';
import { useComplianceCountry } from '../../../../hooks/useComplianceCountry';
import {
  getGpssaAutoGenerationSettings,
  updateGpssaAutoGenerationSettings,
} from '../../../../api/Payroll/gpssa';
import type {
  GpssaAutoMode,
  CompanyGrossPayView,
  CompanyGrossPayInput,
  MudadAllowanceCategory,
} from '../../../../models/payroll';
import { GetCompanyGrossPays, EditCompanyGrossPay } from '../../../../api/Payroll/grossPay';
import { GosiRateSchedulePanel } from './components/GosiRateSchedulePanel';
import { CchiTierScheduleTable } from './components/CchiTierScheduleTable';

const ComplianceSettings: React.FC = () => {
  const { t } = useTranslation();
  const complianceCountry = useComplianceCountry();
  const role = useSelector((s: RootState) => s.user.role);
  const isOperator = role === Roles.Operator;
  const { toast, showToast, dismissToast } = useToast();

  // GPSSA auto-generation toggle, persisted via a dedicated endpoint.
  const [gpssaMode, setGpssaMode] = useState<GpssaAutoMode>('Off');
  const [gpssaDay, setGpssaDay] = useState(1);
  const [gpssaLastRunAt, setGpssaLastRunAt] = useState<string | null>(null);
  const [gpssaPendingCount, setGpssaPendingCount] = useState(0);
  const [gpssaSaving, setGpssaSaving] = useState(false);
  const [gpssaDirty, setGpssaDirty] = useState(false);

  const [grossPays, setGrossPays] = useState<CompanyGrossPayView[]>([]);
  const [mudadOverrides, setMudadOverrides] = useState<Record<number, MudadAllowanceCategory | null>>({});
  const [mudadDirty, setMudadDirty] = useState(false);
  const [mudadSaving, setMudadSaving] = useState(false);

  useEffect(() => {
    if (complianceCountry !== 'KSA') return;
    (async () => {
      try {
        const result = await GetCompanyGrossPays();
        if (result.errors.length === 0 && result.payload) {
          setGrossPays(result.payload);
          const init: Record<number, MudadAllowanceCategory | null> = {};
          for (const gp of result.payload) init[gp.id] = gp.mudadCategory ?? null;
          setMudadOverrides(init);
          setMudadDirty(false);
        }
      } catch {
        /* silent — empty card is acceptable */
      }
    })();
  }, [complianceCountry]);

  const setMudadOverride = (id: number, value: MudadAllowanceCategory | null) => {
    setMudadOverrides(prev => ({ ...prev, [id]: value }));
    setMudadDirty(true);
  };

  const handleSaveMudadMapping = async () => {
    setMudadSaving(true);
    try {
      const dirty = grossPays.filter(gp => (gp.mudadCategory ?? null) !== (mudadOverrides[gp.id] ?? null));
      for (const gp of dirty) {
        const payload: CompanyGrossPayInput = {
          id: gp.id,
          name: gp.name,
          description: gp.description,
          amountCalculationType: gp.amountCalculationType,
          type: gp.type,
          taxability: gp.taxability,
          proofRequired: gp.proofRequired,
          amount: gp.amount,
          formulaPercentage: gp.formulaPercentage,
          companyGrossPayForFormulaId: gp.companyGrossPayForFormulaId,
          isPrimary: gp.isPrimary,
          includedInWps: gp.includedInWps,
          isBasicSalary: gp.isBasicSalary ?? false,
          mudadCategory: mudadOverrides[gp.id] ?? null,
        };
        const result = await EditCompanyGrossPay(payload);
        if (result.errors.length > 0) {
          showToast(result.errors[0].description ?? t('common.error', 'Error'), 'error');
          return;
        }
      }
      const reload = await GetCompanyGrossPays();
      if (reload.errors.length === 0 && reload.payload) {
        setGrossPays(reload.payload);
      }
      setMudadDirty(false);
      showToast(t('settings.compliance.mudadMappingSaved', 'Mudad allowance mapping saved.'), 'success');
    } catch {
      showToast(t('settings.compliance.mudadMappingFailed', 'Failed to save Mudad allowance mapping.'), 'error');
    } finally {
      setMudadSaving(false);
    }
  };

  const loadGpssaSettings = useCallback(async () => {
    try {
      const result = await getGpssaAutoGenerationSettings();
      if (result.errors.length === 0 && result.payload) {
        setGpssaMode(result.payload.mode);
        setGpssaDay(result.payload.dayOfMonth);
        setGpssaLastRunAt(result.payload.lastRunAt ?? null);
        setGpssaPendingCount(result.payload.pendingCount ?? 0);
        setGpssaDirty(false);
      }
    } catch {
      // Silent — settings card simply shows defaults.
    }
  }, []);

  useEffect(() => {
    loadGpssaSettings();
  }, [loadGpssaSettings]);

  const handleSaveGpssaAuto = async () => {
    setGpssaSaving(true);
    try {
      const result = await updateGpssaAutoGenerationSettings({ mode: gpssaMode, dayOfMonth: gpssaDay });
      if (result.errors.length === 0 && result.payload) {
        setGpssaLastRunAt(result.payload.lastRunAt ?? null);
        setGpssaPendingCount(result.payload.pendingCount ?? 0);
        setGpssaDirty(false);
        showToast(t('settings.compliance.gpssaAutoSaved', 'Auto-generation settings saved'), 'success');
      } else {
        showToast(result.errors[0]?.message ?? t('common.error'), 'error');
      }
    } catch {
      showToast(t('settings.compliance.gpssaAutoFailed', 'Failed to save auto-generation settings'), 'error');
    } finally {
      setGpssaSaving(false);
    }
  };

  return (
    <PageLayout title={t('settings.compliance.title', 'Compliance Config')} icon={ShieldCheck} accentColor="rose">
      <div className="space-y-6 pb-20">
        {/* Employer identifiers and WPS fields now live in Settings → General → Details. */}

        {/* Mudad Allowance Mapping (KSA-only) */}
        {complianceCountry === 'KSA' && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <div>
                <h3 className="font-semibold">
                  {t('settings.compliance.mudadMapping', 'Mudad SIF Allowance Mapping')}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('settings.compliance.mudadMappingDesc',
                    'The Mudad SIF only accepts allowances sorted into specific columns (Basic, Housing, Transport, Other). Map every component so the wage file is accepted. Anything left unmapped fails the pre-flight check.')}
                </p>
              </div>
              {grossPays.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('settings.compliance.mudadMappingEmpty',
                    'No allowance components configured yet.')}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-muted-foreground">
                        <th className="text-start py-2 font-medium">
                          {t('settings.compliance.mudadColAllowance', 'Allowance')}
                        </th>
                        <th className="text-start py-2 font-medium">
                          {t('settings.compliance.mudadColMapsTo', 'Maps to')}
                        </th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {grossPays.map(gp => {
                        const current = mudadOverrides[gp.id] ?? null;
                        return (
                          <tr key={gp.id} className="border-b border-border last:border-0">
                            <td className="py-2">{gp.name}</td>
                            <td className="py-2">
                              <select
                                value={current ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setMudadOverride(gp.id, v === '' ? null : (v as MudadAllowanceCategory));
                                }}
                                className={`border rounded-md px-2 py-1 bg-background ${current === null ? 'border-red-300' : 'border-input'}`}
                                aria-label={t('settings.compliance.mudadMapping', 'Mudad SIF Allowance Mapping')}
                              >
                                <option value="">{t('settings.compliance.mudadOptSelect', '— Select —')}</option>
                                <option value="Basic">{t('settings.compliance.mudadOptBasic', 'Basic')}</option>
                                <option value="Housing">{t('settings.compliance.mudadOptHousing', 'Housing')}</option>
                                <option value="Transport">{t('settings.compliance.mudadOptTransport', 'Transport')}</option>
                                <option value="Other">{t('settings.compliance.mudadOptOther', 'Other')}</option>
                              </select>
                            </td>
                            <td className="py-2 text-xs">
                              {current === null
                                ? <span className="text-red-600">{t('settings.compliance.mudadUnmappedBadge', 'unmapped (required)')}</span>
                                : <span className="text-emerald-600">{t('settings.compliance.mudadMappedBadge', 'mapped')}</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {mudadDirty && (
                <div className="flex justify-end">
                  <Button onClick={handleSaveMudadMapping} disabled={mudadSaving}>
                    {mudadSaving
                      ? t('common.saving', 'Saving...')
                      : t('settings.compliance.mudadMappingSave', 'Save mapping')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* GOSI Rate Schedule & CCHI Tier Schedule — global, operator-managed reference
            data (no CompanyId). Shown to the Operator regardless of company/country, and
            hidden from HR (who sees the resulting rates read-only under Payroll → Pension). */}
        {isOperator && complianceCountry !== 'KSA' && complianceCountry !== 'UAE' && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              {t('settings.compliance.operatorGlobalNote', 'These GOSI and CCHI rate schedules are platform-wide reference tables shared by every company. Edits here apply globally. Company-specific compliance settings appear when viewing a KSA or UAE company.')}
            </p>
          </div>
        )}

        {isOperator && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 space-y-2">
                <p className="font-medium">{t('settings.compliance.operatorGosiGuide.title', 'How to manage GOSI rates')}</p>
                <ol className="list-decimal ms-4 space-y-1 text-xs">
                  <li>{t('settings.compliance.operatorGosiGuide.step1', 'Click "+ Add Row" in the GOSI Rate Schedule below.')}</li>
                  <li>{t('settings.compliance.operatorGosiGuide.step2', 'Select the scheme (Legacy for employees hired before 3 Jul 2024, NewPhased for newer hires).')}</li>
                  <li>{t('settings.compliance.operatorGosiGuide.step3', 'Set the branch (ANI, OH, or SANED), employer and employee rates as decimals (e.g. 0.09 for 9%), the wage cap (SAR 45,000), and the effective date.')}</li>
                  <li>{t('settings.compliance.operatorGosiGuide.step4', 'Leave "Effective To" blank for the current open-ended rate. The system picks the matching rate by payroll period date.')}</li>
                </ol>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 space-y-2">
                <p className="font-medium">{t('settings.compliance.operatorCchiGuide.title', 'How to manage CCHI tiers')}</p>
                <ol className="list-decimal ms-4 space-y-1 text-xs">
                  <li>{t('settings.compliance.operatorCchiGuide.step1', 'Click "+ Add Row" in the CCHI Tier Schedule below.')}</li>
                  <li>{t('settings.compliance.operatorCchiGuide.step2', 'Select a visa category (Senior Executive, Professional, Skilled, Semi-Skilled, or Domestic).')}</li>
                  <li>{t('settings.compliance.operatorCchiGuide.step3', 'Set the monthly salary range (min and max). Leave max blank to cover all salaries above the min.')}</li>
                  <li>{t('settings.compliance.operatorCchiGuide.step4', 'Choose the expected CCHI plan tier (Class C / B / A / VIP). The system flags employees whose actual policy tier does not match.')}</li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {isOperator && (
          <GosiRateSchedulePanel onError={(msg) => showToast(msg, 'error')} />
        )}

        {isOperator && (
          <CchiTierScheduleTable onError={(msg) => showToast(msg, 'error')} />
        )}

        {/* GOSI Contributions reference (KSA-only) */}
        {complianceCountry === 'KSA' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Landmark className="w-5 h-5 text-blue-500" />
                <h3 className="font-semibold text-lg">{t('settings.compliance.ksaGosi', 'GOSI Contributions (ANI / OH / SANED)')}</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {t('settings.compliance.ksaGosiDesc', 'Saudi statutory social-insurance branches. Exact rates are operator-managed in the GOSI Rate Schedule above.')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
                  <h4 className="font-medium text-blue-800 mb-2">{t('settings.compliance.ksaAni', 'ANI (Annuities)')}</h4>
                  <p className="text-sm text-blue-700">{t('settings.compliance.ksaAniDesc', 'Saudis only · base basic + housing · cap SAR 45,000. Legacy 9% + 9%; new hires (post-3 Jul 2024) phase to 11% + 11% by 2028.')}</p>
                </div>
                <div className="p-4 rounded-lg border bg-emerald-50 border-emerald-200">
                  <h4 className="font-medium text-emerald-800 mb-2">{t('settings.compliance.ksaOh', 'OH (Occupational Hazards)')}</h4>
                  <p className="text-sm text-emerald-700">{t('settings.compliance.ksaOhDesc', 'All employees incl. non-Saudis · 2% employer-only · base basic + housing · cap SAR 45,000.')}</p>
                </div>
                <div className="p-4 rounded-lg border bg-purple-50 border-purple-200">
                  <h4 className="font-medium text-purple-800 mb-2">{t('settings.compliance.ksaSaned', 'SANED (Unemployment)')}</h4>
                  <p className="text-sm text-purple-700">{t('settings.compliance.ksaSanedDesc', 'Saudis only · 0.75% + 0.75% (1.5% total, since Jan 2022) · cap SAR 45,000.')}</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">{t('settings.compliance.ksaGosiLaw', 'Social Insurance Law: Royal Decree M/22 (1421H). SANED rate adjusted 1 Jan 2022.')}</p>
            </CardContent>
          </Card>
        )}

        {/* CCHI Health Insurance reference (KSA-only) */}
        {complianceCountry === 'KSA' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <HeartPulse className="w-5 h-5 text-rose-500" />
                <h3 className="font-semibold text-lg">{t('settings.compliance.ksaCchi', 'Cooperative Health Insurance (CCHI)')}</h3>
              </div>
              <div className="p-4 rounded-lg border bg-rose-50 border-rose-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-rose-800">
                    <p className="font-medium mb-1">{t('settings.compliance.ksaCchiLaw', 'Cooperative Health Insurance Law: Royal Decree M/10 (1420H / 1999)')}</p>
                    <p>{t('settings.compliance.ksaCchiDesc', 'Mandatory medical cover for non-Saudi employees and dependents. Plan tiers Class C / B / A / VIP are matched to visa category and salary range. Renewal alerts fire at 90 / 60 / 30 / 7 days. Expired cover blocks Iqama renewal, which freezes payroll. The tier matrix is operator-managed in the CCHI Tier Schedule above.')}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mudad Wage Protection reference (KSA-only) */}
        {complianceCountry === 'KSA' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Building className="w-5 h-5 text-emerald-500" />
                <h3 className="font-semibold text-lg">{t('settings.compliance.ksaMudad', 'Mudad Wage Protection (WPS)')}</h3>
              </div>
              <div className="p-4 rounded-lg border bg-emerald-50 border-emerald-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-emerald-800">
                    <p className="font-medium mb-1">{t('settings.compliance.ksaMudadLaw', 'HRSD WPS Wages File Technical Specification; Mudad portal')}</p>
                    <p>{t('settings.compliance.ksaMudadDesc', 'Monthly wages upload to Mudad as a Salary Information File (SIF) within 30 days of the wage due date (reduced from 60, effective 1 Mar 2025). Reminder ladder: day 0, day 10, day 15, day 20, then the upload window closes at day 30.')}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Overtime Classification reference (KSA-only) */}
        {complianceCountry === 'KSA' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-indigo-500" />
                <h3 className="font-semibold text-lg">{t('settings.compliance.overtime', 'Overtime Classification')}</h3>
              </div>
              <div className="p-4 rounded-lg border bg-indigo-50 border-indigo-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-indigo-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-indigo-800">
                    <p className="font-medium mb-1">{t('settings.compliance.ksaOtLaw', 'KSA Labor Law: Article 107')}</p>
                    <p>{t('settings.compliance.ksaOtDesc', 'Overtime = 100% of the actual hourly wage (basic + fixed allowances) + 50% of the basic hourly wage. The common "150% of basic" shortcut under-pays when allowances exist.')}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Probation reference (KSA-only) */}
        {complianceCountry === 'KSA' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-5 h-5 text-blue-500" />
                <h3 className="font-semibold text-lg">{t('settings.compliance.probation', 'Probation Period')}</h3>
              </div>
              <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">{t('settings.compliance.ksaProbationLaw', 'KSA Labor Law: Articles 53–54')}</p>
                    <p>{t('settings.compliance.ksaProbationDesc', 'Probation may not exceed 90 days, extendable to a total of 180 days only with the employee\'s written consent.')}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* End of Service reference (KSA-only) */}
        {complianceCountry === 'KSA' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Landmark className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold text-lg">{t('settings.compliance.ksaEos', 'End of Service')}</h3>
              </div>
              <div className="p-4 rounded-lg border bg-amber-50 border-amber-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">{t('settings.compliance.ksaEosLaw', 'KSA Labor Law: Articles 84–85')}</p>
                    <p>{t('settings.compliance.ksaEosDesc', 'Half a month\'s wage per year for the first 5 years, then a full month per year thereafter, computed on the last actual wage (basic + all fixed/regular allowances). Resignation factors: 0% (<2y), 33% (2–5y), 67% (5–10y), 100% (10y+).')}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* WPS fields (MOL, bank routing, threshold) now live in Settings → General → Details. */}

        {/* GPSSA/ADPF Rates Reference (UAE only) */}
        {complianceCountry === 'UAE' && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Landmark className="w-5 h-5 text-blue-500" />
              <h3 className="font-semibold text-lg">{t('settings.compliance.pensionRates', 'Pension Contribution Rates')}</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              {t('settings.compliance.pensionDescription', 'Statutory pension contribution rates for UAE nationals. These rates are set by law and applied automatically.')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2">{t('settings.compliance.gpssa', 'GPSSA (Non-Abu Dhabi)')}</h4>
                <div className="space-y-1 text-sm text-blue-700">
                  <p>{t('settings.compliance.gpssaEmployeePre', 'Employee (pre-2023): 5%')}</p>
                  <p>{t('settings.compliance.gpssaEmployeePost', 'Employee (post-2023): 11%')}</p>
                  <p>{t('settings.compliance.gpssaEmployer', 'Employer: 15%')}</p>
                  <p>{t('settings.compliance.gpssaGovt', 'Government: 6%')}</p>
                </div>
              </div>
              <div className="p-4 rounded-lg border bg-purple-50 border-purple-200">
                <h4 className="font-medium text-purple-800 mb-2">{t('settings.compliance.adpf', 'ADPF (Abu Dhabi)')}</h4>
                <div className="space-y-1 text-sm text-purple-700">
                  <p>{t('settings.compliance.adpfEmployee', 'Employee: 5%')}</p>
                  <p>{t('settings.compliance.adpfEmployer', 'Employer: 15%')}</p>
                  <p>{t('settings.compliance.adpfGovt', 'Government: 6%')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* GPSSA / ADPF Auto-Generation Toggle (UAE only) */}
        {complianceCountry === 'UAE' && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-lg">
                {t('settings.compliance.gpssaAutoTitle', 'GPSSA / ADPF Auto-Generation')}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              {t(
                'settings.compliance.gpssaAutoDescription',
                'Choose how the system creates each month\'s pension contributions. Submission to GPSSA / ADPF portals always remains a manual step.'
              )}
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {(['Off', 'AutoPreview', 'AutoCommit'] as GpssaAutoMode[]).map(mode => {
                const selected = gpssaMode === mode;
                const titles: Record<GpssaAutoMode, string> = {
                  Off: t('settings.compliance.gpssaAutoOff', 'Manual Only'),
                  AutoPreview: t('settings.compliance.gpssaAutoPreview', 'Auto-Preview'),
                  AutoCommit: t('settings.compliance.gpssaAutoCommit', 'Auto-Commit'),
                };
                const bodies: Record<GpssaAutoMode, string> = {
                  Off: t(
                    'settings.compliance.gpssaAutoOffBody',
                    'You click Generate Contributions in the Compliance Center each month. Default, safest for setups still in flux.'
                  ),
                  AutoPreview: t(
                    'settings.compliance.gpssaAutoPreviewBody',
                    'Scheduler creates a pending run on the configured day. An admin must Approve before payroll can use it. Recommended.'
                  ),
                  AutoCommit: t(
                    'settings.compliance.gpssaAutoCommitBody',
                    'Scheduler creates and commits contributions automatically. Aborts if any employee would be skipped. Use only when payroll setup is fully stabilized.'
                  ),
                };
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => {
                      setGpssaMode(mode);
                      setGpssaDirty(true);
                    }}
                    className={`text-start p-4 rounded-lg border-2 transition-colors ${
                      selected
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-input hover:border-muted-foreground hover:bg-muted/40'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{titles[mode]}</span>
                      {selected && (
                        <span className="text-xs px-2 py-0.5 bg-amber-500 text-white rounded-full">
                          {t('common.selected', 'Selected')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{bodies[mode]}</p>
                  </button>
                );
              })}
            </div>

            {gpssaMode !== 'Off' && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    {t('settings.compliance.gpssaDay', 'Run on day of month')}
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={28}
                    value={gpssaDay}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v)) {
                        setGpssaDay(Math.min(28, Math.max(1, v)));
                        setGpssaDirty(true);
                      }
                    }}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t(
                      'settings.compliance.gpssaDayHint',
                      'Generates the prior month\'s contributions on or after this day. 1–28.'
                    )}
                  </p>
                </div>
                <div className="md:col-span-2 text-sm text-muted-foreground">
                  {gpssaLastRunAt ? (
                    <p>
                      {t('settings.compliance.gpssaLastRun', 'Last automatic run: {{when}}', {
                        when: new Date(gpssaLastRunAt).toLocaleString(),
                      })}
                    </p>
                  ) : (
                    <p>{t('settings.compliance.gpssaNeverRun', 'Auto-generation has not run yet.')}</p>
                  )}
                  {gpssaPendingCount > 0 && (
                    <p className="text-amber-700 mt-1 inline-flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      {t(
                        'settings.compliance.gpssaPendingHint',
                        '{{count}} pending contribution(s) waiting for approval. Approve them from the Compliance Center → Pension tab.',
                        { count: gpssaPendingCount }
                      )}
                    </p>
                  )}
                </div>
              </div>
            )}

            {gpssaDirty && (
              <div className="mt-4 flex justify-end">
                <Button onClick={handleSaveGpssaAuto} disabled={gpssaSaving}>
                  {gpssaSaving
                    ? t('common.saving', 'Saving...')
                    : t('settings.compliance.gpssaAutoSave', 'Save auto-generation settings')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Deduction Cap Reference (UAE only) */}
        {complianceCountry === 'UAE' && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-lg">{t('settings.compliance.deductionCap', 'Deduction Cap Rule')}</h3>
            </div>
            <div className="p-4 rounded-lg border bg-amber-50 border-amber-200">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">{t('settings.compliance.article25', 'UAE Labour Law: Article 25')}</p>
                  <p>
                    {t('settings.compliance.deductionCapDesc', 'Total deductions from an employee\'s wage shall not exceed 50% of gross salary. This includes loan repayments, court-ordered deductions, and voluntary deductions. The system automatically flags any breaches in the Compliance Dashboard.')}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Overtime Classification (UAE only) */}
        {complianceCountry === 'UAE' && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-indigo-500" />
              <h3 className="font-semibold text-lg">{t('settings.compliance.overtime', 'Overtime Classification')}</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              {t('settings.compliance.overtimeDescription', 'Outside-hours work is monetized on each monthly payslip using the single Basic Salary component and the statutory multipliers below.')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border bg-indigo-50 border-indigo-200">
                <h4 className="font-medium text-indigo-800 mb-2">{t('settings.compliance.otRegular', 'Regular Overtime (1.25×)')}</h4>
                <div className="space-y-1 text-sm text-indigo-700">
                  <p>{t('settings.compliance.otRegularRule', 'Weekday outside-hours work (FDL 33/2021, Art. 19).')}</p>
                  <p>{t('settings.compliance.otDivisor', 'Hourly rate = Basic Salary ÷ 208.')}</p>
                </div>
              </div>
              <div className="p-4 rounded-lg border bg-rose-50 border-rose-200">
                <h4 className="font-medium text-rose-800 mb-2">{t('settings.compliance.otPremium', 'Premium Overtime (1.50×)')}</h4>
                <div className="space-y-1 text-sm text-rose-700">
                  <p>{t('settings.compliance.otPremiumRule', 'Friday rest day, public holidays, and the night window 21:00–04:00 (Art. 19/20/21).')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Public Holidays (read-only, country-agnostic) */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-emerald-500" />
                <h3 className="font-semibold text-lg">{t('settings.compliance.holidays', 'Public Holidays')}</h3>
              </div>
              <Link
                to="/settings/attendance/holidays"
                className="text-sm text-primary hover:underline"
              >
                {t('settings.compliance.manageHolidays', 'Manage holidays →')}
              </Link>
            </div>
            <div className="p-4 rounded-lg border bg-emerald-50 border-emerald-200">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <div className="text-sm text-emerald-800">
                  <p className="font-medium mb-1">{t('settings.compliance.holidaysHeading', 'How holidays affect payroll')}</p>
                  <p>
                    {t('settings.compliance.holidaysDesc', 'Mandatory public holidays configured for your country are used to classify outside-hours work on that day as Premium Overtime (1.50×). Non-mandatory days are treated as regular weekdays. Keep the holiday calendar current to ensure OT monetization reflects statutory rates.')}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Probation Reference (UAE only) */}
        {complianceCountry === 'UAE' && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Info className="w-5 h-5 text-blue-500" />
              <h3 className="font-semibold text-lg">{t('settings.compliance.probation', 'Probation Period')}</h3>
            </div>
            <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">{t('settings.compliance.article9', 'UAE Labour Law: Article 9')}</p>
                  <p>
                    {t('settings.compliance.probationDesc', 'The probation period shall not exceed 6 months (183 days). The system monitors employees on probation and alerts when they approach or exceed this limit via the Compliance Dashboard.')}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
};

export default ComplianceSettings;
